import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, User, AlertTriangle, Loader2, Users, Plus, Trash2, FileText, Download } from "lucide-react";
import api from "../utils/api";
import toast from "react-hot-toast";

function fmtDate(iso) {
  return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

/* ── Invoices ────────────────────────────────────────────── */
function InvoicesSection() {
  const [invoices, setInvoices] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [openingId, setOpeningId] = useState(null);

  useEffect(() => {
    api.get("/api/payment/invoices")
      .then(({ data }) => setInvoices(data))
      .catch(() => {}) // no invoices yet, or not the owner — fail quiet
      .finally(() => setLoading(false));
  }, []);

  async function viewInvoice(inv) {
    setOpeningId(inv.id);
    try {
      const { data } = await api.get(`/api/payment/invoices/${inv.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      window.open(url, "_blank");
    } catch (err) {
      toast.error("Could not open invoice PDF");
    } finally {
      setOpeningId(null);
    }
  }

  if (loading) {
    return <div style={{ textAlign: "center", padding: 16 }}><Loader2 size={16} style={{ animation: "spin 1s linear infinite", color: "#9CA3AF" }} /></div>;
  }
  if (!invoices.length) return null;

  return (
    <div style={{ marginBottom: 22 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12.5, fontWeight: 700, color: "#374151", marginBottom: 10 }}>
        <FileText size={13} /> Invoices
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {invoices.map(inv => {
          const statusStyle =
            inv.status === "paid"      ? { bg: "#F0FDF4", color: "#16A34A", border: "#BBF7D0", label: "Paid" } :
            inv.status === "cancelled" ? { bg: "#FEF2F2", color: "#DC2626", border: "#FECACA", label: "Cancelled" } :
                                          { bg: "#EFF6FF", color: "#2563EB", border: "#BFDBFE", label: "Sent" };
          return (
            <div key={inv.id} style={{ background: "#F9FAFB", border: "1px solid #F3F4F6", borderRadius: 10, padding: "12px 14px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 3 }}>
                    <span style={{ fontFamily: "monospace", fontSize: 12, fontWeight: 700, color: "#111827" }}>{inv.invoice_number}</span>
                    <span style={{ fontSize: 10, fontWeight: 700, background: statusStyle.bg, color: statusStyle.color, border: `1px solid ${statusStyle.border}`, borderRadius: 20, padding: "2px 8px" }}>{statusStyle.label}</span>
                  </div>
                  <div style={{ fontSize: 11, color: "#9CA3AF" }}>{inv.calls_qty} calls · {fmtDate(inv.created_at)}</div>
                </div>
                <div style={{ textAlign: "right", flexShrink: 0, marginLeft: 12 }}>
                  <div style={{ fontSize: 15, fontWeight: 800, color: "#111827" }}>
                    ₹{Number(inv.total).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                  </div>
                  <button
                    onClick={() => viewInvoice(inv)}
                    disabled={openingId === inv.id}
                    style={{ marginTop: 6, display: "flex", alignItems: "center", gap: 4, fontSize: 10, fontWeight: 600, background: "#EEF2FF", color: "#4F46E5", border: "1px solid #C7D2FE", borderRadius: 5, padding: "3px 8px", cursor: openingId === inv.id ? "default" : "pointer", marginLeft: "auto" }}
                  >
                    {openingId === inv.id ? <Loader2 size={9} style={{ animation: "spin 1s linear infinite" }} /> : <Download size={9} />}
                    View PDF
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function TeamSection() {
  const [team,    setTeam]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [name,     setName]     = useState("");
  const [email,    setEmail]    = useState("");
  const [password, setPassword] = useState("");
  const [inviting, setInviting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/api/v/users");
      setTeam(data);
    } catch {
      // owner-only endpoint — silently ignore for non-owner sessions
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function handleInvite(e) {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !password) return toast.error("All fields required");
    setInviting(true);
    try {
      await api.post("/api/v/users", { name: name.trim(), email: email.trim(), password });
      toast.success("Team member added");
      setName(""); setEmail(""); setPassword(""); setShowInvite(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to add team member");
    } finally {
      setInviting(false);
    }
  }

  async function handleRemove(member) {
    if (!window.confirm(`Remove ${member.name} from your team?`)) return;
    try {
      await api.delete(`/api/v/users/${member.id}`);
      setTeam(prev => prev.filter(m => m.id !== member.id));
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to remove team member");
    }
  }

  return (
    <div style={{ marginBottom: 22 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12.5, fontWeight: 700, color: "#374151" }}>
          <Users size={13} /> Team
        </div>
        <button
          onClick={() => setShowInvite(v => !v)}
          style={{ display: "flex", alignItems: "center", gap: 4, background: "none", border: "none", color: "#4F46E5", fontSize: 12, fontWeight: 700, cursor: "pointer" }}
        >
          <Plus size={12} /> Add
        </button>
      </div>

      {showInvite && (
        <form onSubmit={handleInvite} style={{
          background: "#F9FAFB", border: "1px solid #F3F4F6", borderRadius: 10,
          padding: 12, marginBottom: 10, display: "flex", flexDirection: "column", gap: 8,
        }}>
          <input placeholder="Name" value={name} onChange={e => setName(e.target.value)}
            style={{ height: 34, padding: "0 10px", border: "1px solid #E5E7EB", borderRadius: 7, fontSize: 12.5, outline: "none" }} />
          <input placeholder="Email" value={email} onChange={e => setEmail(e.target.value)}
            style={{ height: 34, padding: "0 10px", border: "1px solid #E5E7EB", borderRadius: 7, fontSize: 12.5, outline: "none" }} />
          <input placeholder="Temporary password" type="password" value={password} onChange={e => setPassword(e.target.value)}
            style={{ height: 34, padding: "0 10px", border: "1px solid #E5E7EB", borderRadius: 7, fontSize: 12.5, outline: "none" }} />
          <button type="submit" disabled={inviting}
            style={{ height: 34, borderRadius: 7, border: "none", background: "#4F46E5", color: "#fff", fontSize: 12.5, fontWeight: 700, cursor: "pointer" }}>
            {inviting ? "Adding…" : "Add dispatcher"}
          </button>
        </form>
      )}

      {loading ? (
        <div style={{ fontSize: 12, color: "#9CA3AF" }}>Loading…</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {team.map(member => (
            <div key={member.id} style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              background: "#F9FAFB", border: "1px solid #F3F4F6", borderRadius: 8, padding: "8px 10px",
            }}>
              <div>
                <div style={{ fontSize: 12.5, fontWeight: 600, color: "#111827" }}>{member.name}</div>
                <div style={{ fontSize: 11, color: "#9CA3AF" }}>{member.email}</div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{
                  fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 99,
                  background: member.role === "owner" ? "#EEF2FF" : "#F0FDF4",
                  color:      member.role === "owner" ? "#4F46E5" : "#16A34A",
                }}>{member.role}</span>
                {member.role !== "owner" && (
                  <button
                    onClick={() => handleRemove(member)}
                    title="Remove"
                    style={{ background: "none", border: "none", color: "#DC2626", cursor: "pointer", display: "flex" }}
                  ><Trash2 size={13} /></button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AccountModal({ org, onClose, onDeleted }) {
  const isOwner = (org?.role || "owner") === "owner";
  const [confirming, setConfirming] = useState(false);
  const [password,   setPassword]   = useState("");
  const [typedDelete, setTypedDelete] = useState("");
  const [deleting,   setDeleting]   = useState(false);
  const [error,      setError]      = useState("");

  const canDelete = password.length > 0 && typedDelete.trim().toUpperCase() === "DELETE";

  async function handleDelete() {
    if (!canDelete || deleting) return;
    setDeleting(true);
    setError("");
    try {
      await api.delete("/api/v/account", { data: { password } });
      toast.success("Account deleted.");
      onDeleted();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to delete account.");
      setDeleting(false);
    }
  }

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        transition={{ duration: 0.15 }}
        style={{
          position: "fixed", inset: 0, zIndex: 900,
          background: "rgba(17,24,39,0.55)",
          display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
        }}
        onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      >
        <motion.div
          initial={{ scale: 0.96, y: 16, opacity: 0 }}
          animate={{ scale: 1,    y: 0,  opacity: 1 }}
          exit={{    scale: 0.96, y: 16, opacity: 0 }}
          transition={{ duration: 0.18, ease: "easeOut" }}
          style={{
            width: "100%", maxWidth: 440,
            background: "#ffffff", border: "1px solid #E5E7EB",
            borderRadius: 16, overflow: "hidden",
            boxShadow: "0 24px 64px rgba(0,0,0,0.14)",
            maxHeight: "90vh", display: "flex", flexDirection: "column",
          }}
        >
          {/* Header */}
          <div style={{
            padding: "18px 22px", borderBottom: "1px solid #F3F4F6",
            display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0,
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ width: 34, height: 34, borderRadius: 9, background: "#EFF6FF", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <User size={16} color="#2563EB" />
              </div>
              <div>
                <div style={{ fontSize: 15, fontWeight: 700, color: "#111827" }}>Account</div>
                <div style={{ fontSize: 12, color: "#9CA3AF", marginTop: 1 }}>Manage your account</div>
              </div>
            </div>
            <button
              onClick={onClose}
              style={{ background: "none", border: "1px solid #E5E7EB", borderRadius: 8, width: 32, height: 32, cursor: "pointer", color: "#9CA3AF", display: "flex", alignItems: "center", justifyContent: "center" }}
            ><X size={14} /></button>
          </div>

          {/* Body */}
          <div style={{ flex: 1, overflowY: "auto", padding: "18px 22px 22px" }}>
            <div style={{
              background: "#F9FAFB", border: "1px solid #F3F4F6", borderRadius: 10,
              padding: "12px 14px", marginBottom: 22, display: "flex", flexDirection: "column", gap: 8,
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
                <span style={{ color: "#9CA3AF" }}>Name</span>
                <span style={{ color: "#111827", fontWeight: 600 }}>{org?.name}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
                <span style={{ color: "#9CA3AF" }}>Email</span>
                <span style={{ color: "#111827", fontWeight: 600 }}>{org?.email}</span>
              </div>
              {org?.phone && (
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
                  <span style={{ color: "#9CA3AF" }}>Phone</span>
                  <span style={{ color: "#111827", fontWeight: 600 }}>{org.phone}</span>
                </div>
              )}
              {org?.billingAddress && (
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, gap: 12 }}>
                  <span style={{ color: "#9CA3AF", flexShrink: 0 }}>Billing Address</span>
                  <span style={{ color: "#111827", fontWeight: 600, textAlign: "right" }}>{org.billingAddress}</span>
                </div>
              )}
              {org?.gstNumber && (
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
                  <span style={{ color: "#9CA3AF" }}>GSTIN</span>
                  <span style={{ color: "#111827", fontWeight: 600 }}>{org.gstNumber}</span>
                </div>
              )}
              {!org?.gstNumber && org?.panNumber && (
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
                  <span style={{ color: "#9CA3AF" }}>PAN</span>
                  <span style={{ color: "#111827", fontWeight: 600 }}>{org.panNumber}</span>
                </div>
              )}
            </div>

            {isOwner && <InvoicesSection />}

            {isOwner && <TeamSection />}

            {/* Danger zone — owner-only */}
            {isOwner && (
            <div style={{ border: "1px solid #FECACA", borderRadius: 10, overflow: "hidden" }}>
              <div style={{ background: "#FEF2F2", padding: "10px 14px", display: "flex", alignItems: "center", gap: 8 }}>
                <AlertTriangle size={14} color="#DC2626" />
                <span style={{ fontSize: 12.5, fontWeight: 700, color: "#991B1B" }}>Danger Zone</span>
              </div>
              <div style={{ padding: "14px" }}>
                {!confirming ? (
                  <>
                    <div style={{ fontSize: 12.5, color: "#6B7280", lineHeight: 1.6, marginBottom: 12 }}>
                      Permanently delete your account. Your vehicles and saved data will be removed.
                      This cannot be undone.
                    </div>
                    <button
                      onClick={() => setConfirming(true)}
                      style={{
                        width: "100%", height: 38, borderRadius: 8,
                        background: "#fff", border: "1.5px solid #DC2626", color: "#DC2626",
                        fontSize: 13, fontWeight: 700, cursor: "pointer",
                      }}
                    >
                      Delete My Account
                    </button>
                  </>
                ) : (
                  <>
                    <div style={{ fontSize: 12.5, color: "#6B7280", marginBottom: 10 }}>
                      Enter your password and type <strong>DELETE</strong> to confirm.
                    </div>
                    <input
                      type="password"
                      placeholder="Password"
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      style={{
                        width: "100%", boxSizing: "border-box", height: 36, padding: "0 12px",
                        background: "#F9FAFB", border: "1.5px solid #E5E7EB", borderRadius: 8,
                        fontSize: 13, marginBottom: 8, outline: "none",
                      }}
                    />
                    <input
                      type="text"
                      placeholder='Type "DELETE"'
                      value={typedDelete}
                      onChange={e => setTypedDelete(e.target.value)}
                      style={{
                        width: "100%", boxSizing: "border-box", height: 36, padding: "0 12px",
                        background: "#F9FAFB", border: "1.5px solid #E5E7EB", borderRadius: 8,
                        fontSize: 13, marginBottom: 10, outline: "none",
                      }}
                    />
                    {error && (
                      <div style={{ fontSize: 12, color: "#DC2626", marginBottom: 10 }}>{error}</div>
                    )}
                    <div style={{ display: "flex", gap: 8 }}>
                      <button
                        onClick={() => { setConfirming(false); setPassword(""); setTypedDelete(""); setError(""); }}
                        style={{
                          flex: 1, height: 38, borderRadius: 8,
                          background: "#F9FAFB", border: "1px solid #E5E7EB", color: "#374151",
                          fontSize: 13, fontWeight: 600, cursor: "pointer",
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleDelete}
                        disabled={!canDelete || deleting}
                        style={{
                          flex: 1, height: 38, borderRadius: 8, border: "none",
                          background: canDelete ? "#DC2626" : "#FCA5A5",
                          color: "#fff", fontSize: 13, fontWeight: 700,
                          cursor: canDelete && !deleting ? "pointer" : "default",
                          display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                        }}
                      >
                        {deleting ? <><Loader2 size={14} style={{ animation: "spin 1s linear infinite" }} /> Deleting…</> : "Confirm Delete"}
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
